import { createFileRoute } from "@tanstack/react-router";
import { PageChrome } from "../components/PageChrome";
import { canonical, documentHeaders, faqJsonLd } from "../lib/seo";

/**
 * The App Store listing's Support URL, which is a required field and is checked.
 *
 * It existed nowhere before submission was attempted: `/privacy`, `/terms` and
 * `/delete` were all live and `/support` was a 404, which is a rejection rather
 * than a missing nicety. Apple wants a page a real person can reach us from, so
 * the address is the whole point of it — the rest is answering the questions
 * that would otherwise arrive as one-star reviews.
 *
 * `support@bibleshield.app` is the address already referenced elsewhere in the
 * project; this page is the first thing to publish it.
 */
const SUPPORT_EMAIL = "support@bibleshield.app";

/**
 * The questions this page already shows, as data for the FAQPage schema.
 *
 * Every one of these is rendered below in the same words. That is not a
 * formality — marking up an answer a visitor cannot see is the specific thing
 * Google issues manual actions for, and it is also how AI engines end up
 * quoting a page for something it does not say.
 */
const SUPPORT_FAQ = [
  {
    q: "My apps aren't locking",
    a: "Bible Shield needs Screen Time permission to block anything. Open Settings → Screen Time and make sure Bible Shield is allowed, then check that you have picked apps and at least one lock time in the Locks tab.",
  },
  {
    q: "I need my apps right now",
    a: "Every lock has an emergency unlock. It is meant for the moment you genuinely need the phone, not as a way around the habit — but it is always there and it never asks you to explain yourself.",
  },
  {
    q: "Managing or cancelling your subscription",
    a: "Subscriptions are billed by Apple, so they are managed on your device: Settings → your name → Subscriptions. Cancelling there stops the next renewal and you keep access until the period you already paid for ends.",
  },
  {
    q: "Deleting your account",
    a: "You can erase your account and everything in it from inside the app, at any time, or from bibleshield.app/delete.",
  },
] as const;

export const Route = createFileRoute("/support")({
  headers: documentHeaders,
  head: () => ({
    meta: [
      { "script:ld+json": faqJsonLd([...SUPPORT_FAQ]) },
      { title: "Support — Bible Shield" },
      {
        name: "description",
        content:
          "Get help with Bible Shield: app blocking, prayers, subscriptions and account deletion.",
      },
    ],
    links: [{ rel: "canonical", href: canonical("/support") }],
  }),
  component: Support,
});

function Support() {
  return (
    <PageChrome title="Support">
      <p className="mt-4 max-w-2xl text-[17px] leading-relaxed text-ink2">
        Something not working, or a question about your subscription? Write to
        us and a person will answer.
      </p>

      <div className="mt-8 flex flex-col items-start gap-3">
        <a
          href={`mailto:${SUPPORT_EMAIL}`}
          className="rim inline-flex h-[54px] items-center justify-center bg-brand px-7 font-display text-[17px] font-bold text-onbrand"
        >
          Email {SUPPORT_EMAIL}
        </a>
        <span className="text-[13px] text-ink2">
          We answer in English and Turkish.
        </span>
      </div>

      <section className="mt-12">
        <h2 className="font-display text-[22px] font-bold text-ink">
          Common questions
        </h2>

        <div className="mt-5 max-w-2xl space-y-6 text-[16px] leading-relaxed text-ink2">
          <div>
            <h3 className="font-semibold text-ink">
              My apps aren&rsquo;t locking
            </h3>
            <p className="mt-1.5">
              Bible Shield needs Screen Time permission to block anything. Open
              Settings &rarr; Screen Time and make sure Bible Shield is allowed,
              then check that you have picked apps and at least one lock time in
              the Locks tab.
            </p>
          </div>

          <div>
            <h3 className="font-semibold text-ink">I need my apps right now</h3>
            <p className="mt-1.5">
              Every lock has an emergency unlock. It is meant for the moment you
              genuinely need the phone, not as a way around the habit — but it
              is always there and it never asks you to explain yourself.
            </p>
          </div>

          <div>
            <h3 className="font-semibold text-ink">
              Managing or cancelling your subscription
            </h3>
            <p className="mt-1.5">
              Subscriptions are billed by Apple, so they are managed on your
              device: Settings &rarr; your name &rarr; Subscriptions. Cancelling
              there stops the next renewal and you keep access until the period
              you already paid for ends.
            </p>
          </div>

          <div>
            <h3 className="font-semibold text-ink">Deleting your account</h3>
            <p className="mt-1.5">
              You can erase your account and everything in it from inside the
              app, at any time. See{" "}
              <a
                className="underline underline-offset-2 hover:opacity-70"
                href="/delete"
              >
                bibleshield.app/delete
              </a>
              .
            </p>
          </div>

          <div>
            <h3 className="font-semibold text-ink">
              Reporting something in the community
            </h3>
            <p className="mt-1.5">
              Every post, prayer request and message can be reported from a long
              press. Reports are reviewed and violating content is removed. If
              something needs attention faster than that, email us.
            </p>
          </div>
        </div>
      </section>

      <div className="brutal mt-12 max-w-2xl px-5 py-4 text-[15px] leading-relaxed text-ink2">
        Bible Shield is made by AKES Solutions Limited. See our{" "}
        <a
          className="underline underline-offset-2 hover:opacity-70"
          href="/privacy"
        >
          Privacy Policy
        </a>{" "}
        and{" "}
        <a
          className="underline underline-offset-2 hover:opacity-70"
          href="/terms"
        >
          Terms of Use
        </a>
        .
      </div>
    </PageChrome>
  );
}
